import { codeExample, defineDemoElement, demoLayoutStyles } from './demo-shared.js';
import { css, html, LitElement } from 'lit';
import { state } from 'lit/decorators.js';
import type { DemoSection } from './demo-shared.js';
import type { WebmoduleSelectEvent } from '../../events/webmodule-select.js';

/** Shows the value carried by the menu's webmodule-select event. */
class DemoDropdownSelect extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
      }
    `
  ];

  @state() private lastSelected = '';

  render() {
    return html`
      <div class="demo-row">
        <webmodule-dropdown distance="6">
          <webmodule-button slot="trigger" caret>Actions</webmodule-button>
          <webmodule-menu @webmodule-select=${(e: WebmoduleSelectEvent) => (this.lastSelected = e.detail.item.value)}>
            <webmodule-menu-item value="copy">
              Copy
              <webmodule-icon slot="prefix" name="copy"></webmodule-icon>
            </webmodule-menu-item>
            <webmodule-menu-item value="edit">
              Edit
              <webmodule-icon slot="prefix" name="edit"></webmodule-icon>
            </webmodule-menu-item>
            <webmodule-divider></webmodule-divider>
            <webmodule-menu-item value="delete">
              Delete
              <webmodule-icon slot="prefix" name="bin"></webmodule-icon>
            </webmodule-menu-item>
          </webmodule-menu>
        </webmodule-dropdown>
        <span>
          ${this.lastSelected
            ? html`Last selected: <code>${this.lastSelected}</code>`
            : 'Pick an item to see the webmodule-select payload.'}
        </span>
      </div>
    `;
  }
}

export function registerActionDemos() {
  defineDemoElement('demo-dropdown-select', DemoDropdownSelect);
}

export const buttonSection: DemoSection = {
  id: 'button',
  title: 'Button',
  tags: ['webmodule-button', 'webmodule-button-group'],
  summary:
    'The primary action element. Variants communicate intent, and buttons can render as links, show loading state, ' +
    'or combine into groups.',
  render: () => html`
    ${codeExample({
      label: 'Variants',
      code: `
        <div class="demo-row">
          <webmodule-button variant="default">Default</webmodule-button>
          <webmodule-button variant="primary">Primary</webmodule-button>
          <webmodule-button variant="success">Success</webmodule-button>
          <webmodule-button variant="neutral">Neutral</webmodule-button>
          <webmodule-button variant="warning">Warning</webmodule-button>
          <webmodule-button variant="danger">Danger</webmodule-button>
          <webmodule-button variant="text">Text</webmodule-button>
        </div>
        <div class="demo-row">
          <webmodule-button variant="default" outline>Default</webmodule-button>
          <webmodule-button variant="primary" outline>Primary</webmodule-button>
          <webmodule-button variant="success" outline>Success</webmodule-button>
          <webmodule-button variant="neutral" outline>Neutral</webmodule-button>
          <webmodule-button variant="warning" outline>Warning</webmodule-button>
          <webmodule-button variant="danger" outline>Danger</webmodule-button>
        </div>`
    })}
    ${codeExample({
      label: 'States',
      code: `
        <div class="demo-row">
          <webmodule-button variant="primary" disabled>Disabled</webmodule-button>
          <webmodule-button variant="primary" loading>Loading</webmodule-button>
          <webmodule-button variant="success" loading>Loading</webmodule-button>
        </div>`
    })}
    ${codeExample({
      label: 'Sizes and shapes',
      code: `
        <div class="demo-row">
          <webmodule-button size="small">Small</webmodule-button>
          <webmodule-button size="medium">Medium</webmodule-button>
          <webmodule-button size="large">Large</webmodule-button>
        </div>
        <div class="demo-row">
          <webmodule-button size="small" pill>Small pill</webmodule-button>
          <webmodule-button size="medium" pill>Medium pill</webmodule-button>
          <webmodule-button size="small" circle><webmodule-icon name="eye"></webmodule-icon></webmodule-button>
          <webmodule-button size="medium" circle><webmodule-icon name="eye"></webmodule-icon></webmodule-button>
        </div>`
    })}
    ${codeExample({
      label: 'Slots and extras',
      code: `
        <div class="demo-row">
          <webmodule-button>
            <webmodule-icon slot="prefix" name="edit"></webmodule-icon>
            With prefix icon
          </webmodule-button>
          <webmodule-button>
            Inbox
            <webmodule-badge>30</webmodule-badge>
          </webmodule-button>
          <webmodule-button variant="text" href="#/button">Link button (href)</webmodule-button>
        </div>`
    })}
    ${codeExample({
      label: 'Button group',
      code: `
        <div class="demo-row">
          <webmodule-button-group>
            <webmodule-button>Left</webmodule-button>
            <webmodule-button>Middle</webmodule-button>
            <webmodule-button>Right</webmodule-button>
          </webmodule-button-group>
        </div>`
    })}
  `
};

export const iconButtonSection: DemoSection = {
  id: 'icon-button',
  title: 'Icon Button',
  tags: ['webmodule-icon-button'],
  summary:
    'A minimal button rendering a single icon - the standard control for table row actions and header toolbars. ' +
    'Always set label for assistive technology.',
  render: () => html`
    ${codeExample({
      label: 'Basics',
      code: `
        <div class="demo-row">
          <webmodule-icon-button name="edit" label="Edit"></webmodule-icon-button>
          <webmodule-icon-button name="copy" label="Copy"></webmodule-icon-button>
          <webmodule-icon-button name="bin" label="Delete"></webmodule-icon-button>
          <webmodule-icon-button name="bin" label="Delete" disabled></webmodule-icon-button>
          <webmodule-icon-button name="eye" label="View" style="font-size: 1.5rem;"></webmodule-icon-button>
        </div>`
    })}
    ${codeExample({
      label: 'With a tooltip',
      code: `
        <div class="demo-row">
          <webmodule-tooltip content="Copy to clipboard">
            <webmodule-icon-button name="copy" label="Copy to clipboard"></webmodule-icon-button>
          </webmodule-tooltip>
        </div>`
    })}
  `
};

export const dropdownSection: DemoSection = {
  id: 'dropdown',
  title: 'Dropdown & Menu',
  tags: ['webmodule-dropdown', 'webmodule-menu', 'webmodule-menu-item', 'webmodule-menu-label'],
  summary:
    'Dropdown anchors a floating panel to a trigger; menu supplies the list semantics inside it. Menu items emit a ' +
    'webmodule-select event on the menu with the chosen item.',
  render: () => html`
    <demo-example
      label="Selection events"
      .code=${`<webmodule-menu @webmodule-select=\${(e: WebmoduleSelectEvent) => use(e.detail.item.value)}>...`}
    >
      <demo-dropdown-select></demo-dropdown-select>
    </demo-example>

    ${codeExample({
      label: 'Menu anatomy',
      description: 'Labels, dividers, checkbox items, disabled items, prefix/suffix icons and nested submenus.',
      code: `
        <webmodule-menu style="max-width: 240px;">
          <webmodule-menu-label>Section label</webmodule-menu-label>
          <webmodule-menu-item>Plain item</webmodule-menu-item>
          <webmodule-menu-item type="checkbox" checked>Checkbox item</webmodule-menu-item>
          <webmodule-menu-item disabled>Disabled item</webmodule-menu-item>
          <webmodule-divider></webmodule-divider>
          <webmodule-menu-item>
            Prefix icon
            <webmodule-icon slot="prefix" name="bin"></webmodule-icon>
          </webmodule-menu-item>
          <webmodule-menu-item>
            Submenu
            <webmodule-menu slot="submenu">
              <webmodule-menu-item value="find">Find...</webmodule-menu-item>
              <webmodule-menu-item value="find-next">Find Next</webmodule-menu-item>
            </webmodule-menu>
          </webmodule-menu-item>
        </webmodule-menu>`
    })}
    ${codeExample({
      label: 'Trigger sizes and placement',
      code: `
        <div class="demo-row">
          <webmodule-dropdown>
            <webmodule-button slot="trigger" caret size="small">Small</webmodule-button>
            <webmodule-menu>
              <webmodule-menu-item>Item 1</webmodule-menu-item>
              <webmodule-menu-item>Item 2</webmodule-menu-item>
            </webmodule-menu>
          </webmodule-dropdown>
          <webmodule-dropdown>
            <webmodule-button slot="trigger" caret size="medium">Medium</webmodule-button>
            <webmodule-menu>
              <webmodule-menu-item>Item 1</webmodule-menu-item>
              <webmodule-menu-item>Item 2</webmodule-menu-item>
            </webmodule-menu>
          </webmodule-dropdown>
          <webmodule-dropdown placement="top-start" distance="8">
            <webmodule-button slot="trigger" caret>Opens upward</webmodule-button>
            <webmodule-menu>
              <webmodule-menu-item>Item 1</webmodule-menu-item>
              <webmodule-menu-item>Item 2</webmodule-menu-item>
            </webmodule-menu>
          </webmodule-dropdown>
        </div>`
    })}
  `
};
